import { FormEvent, ReactNode, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle, ClipboardList, Download, FileText, History, Info, Loader2, PackageCheck, Plus, RotateCcw, Save, Trash2 } from "lucide-react";
import { useBusiness } from "../../contexts/BusinessContext";
import { useAuth } from "../../contexts/AuthContext";
import {
  createParTemplateItem,
  deleteParCheck,
  deleteParTemplateItem,
  fetchBusinessDetails,
  fetchParChecks,
  fetchParSupplierItems,
  fetchParTemplate,
  fetchSuppliers,
  submitParCheck,
  updateParTemplateItem,
  type ParCheck,
  type ParSupplierItem,
  type ParTemplate,
  type ParTemplateItem,
} from "../../services/apiService";
import { getCategoriesForIndustry } from "../../data/categories";
import type { Supplier } from "../../types";

const NAVY = "#2a2420";
const ORANGE = "#607840";
const SURFACE = "#fff";
const RAISED = "#fff";
const CANVAS = "#fff";
const LINE = "#e9e1d4";
const MUTED = "#8a8074";
const CHIP = "#f3ece1";
const SHADOW = "0 1px 3px rgba(42,36,32,.08), 0 12px 28px rgba(42,36,32,.06)";
const ACCENT_WEAK = "rgba(96,120,64,0.12)";
const ACCENT_LINE = "rgba(96,120,64,0.30)";
const ACCENT_SHADOW = "0 2px 8px rgba(96,120,64,0.25)";
const UP = "#c2412e";
const UP_WEAK = "rgba(194,65,46,0.12)";
const UP_LINE = "rgba(194,65,46,0.24)";
const DOWN = "#3c7d57";
const DOWN_WEAK = "rgba(60,125,87,0.12)";
const DOWN_LINE = "rgba(60,125,87,0.24)";

const PAR_QUERY_OPTIONS = {
  staleTime: 5 * 60 * 1000,
  gcTime: 30 * 60 * 1000,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
} as const;

type ViewMode = "check" | "template" | "history";


function today() {
  return new Date().toISOString().slice(0, 10);
}

function numberValue(value: number | string | null | undefined) {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function displayDate(value: string) {
  return new Date(value).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

function displayDateTime(value: string) {
  return new Date(value).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function groupOrderItems(check: ParCheck) {
  const groups = new Map<string, typeof check.items>();
  check.items
    .filter((item) => item.orderQuantity > 0)
    .forEach((item) => {
      const supplier = item.supplierName || "No supplier";
      groups.set(supplier, [...(groups.get(supplier) ?? []), item]);
    });
  return Array.from(groups.entries()).map(([supplier, items]) => ({ supplier, items }));
}

function escapeHtml(value: string | number | null | undefined) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function buildOrderReportHtml({
  check,
  businessName,
  generatedBy,
}: {
  check: ParCheck;
  businessName: string;
  generatedBy: string;
}) {
  const groups = groupOrderItems(check);
  const generatedAt = new Date().toLocaleString();
  const groupHtml = groups.map(({ supplier, items }) => `
    <section>
      <div class="supplier">
        <h2>${escapeHtml(supplier)}</h2>
        <p>Total estimated order value: Not available</p>
      </div>
      <table>
        <thead>
          <tr>
            <th>Item</th>
            <th>Order line item</th>
            <th>Order qty</th>
            <th>Unit</th>
          </tr>
        </thead>
        <tbody>
          ${items.map((item) => `
            <tr>
              <td>${escapeHtml(item.itemName)}</td>
              <td>${escapeHtml(item.supplierItemDescription)}</td>
              <td>${escapeHtml(item.orderQuantity)}</td>
              <td>${escapeHtml(item.kitchenUnit)}</td>
            </tr>
          `).join("")}
        </tbody>
      </table>
    </section>
  `).join("");

  return `<!doctype html>
    <html>
      <head>
        <title>Order report ${escapeHtml(check.checkDate)}</title>
        <style>
          body { font-family: Arial, sans-serif; color: #2a2420; margin: 32px; }
          h1 { margin: 0 0 6px; font-size: 24px; }
          .meta { color: rgba(80,55,30,0.68); font-size: 12px; line-height: 1.7; margin-bottom: 24px; }
          section { border: 1px solid rgba(80,55,30,0.18); border-radius: 10px; margin: 18px 0; overflow: hidden; break-inside: avoid; }
          .supplier { background: rgba(80,55,30,0.06); padding: 12px 14px; border-bottom: 1px solid rgba(80,55,30,0.18); }
          .supplier h2 { margin: 0; font-size: 16px; }
          .supplier p { margin: 4px 0 0; font-size: 12px; color: rgba(80,55,30,0.65); }
          table { width: 100%; border-collapse: collapse; }
          th, td { text-align: left; padding: 10px 14px; border-bottom: 1px solid rgba(80,55,30,0.10); font-size: 12px; }
          th { text-transform: uppercase; letter-spacing: .08em; font-size: 10px; color: rgba(80,55,30,0.58); }
          tr:last-child td { border-bottom: 0; }
        </style>
      </head>
      <body>
        <h1>Order report</h1>
        <div class="meta">
          Business: ${escapeHtml(businessName)}<br/>
          Check date: ${escapeHtml(displayDate(check.checkDate))}<br/>
          Generated: ${escapeHtml(generatedAt)}<br/>
          Generated by: ${escapeHtml(generatedBy)}
        </div>
        ${groupHtml || "<p>No order items for this check.</p>"}
      </body>
    </html>`;
}

function printOrderReport(check: ParCheck, businessName: string, generatedBy: string) {
  const printWindow = window.open("", "_blank", "noopener,noreferrer,width=900,height=700");
  if (!printWindow) return;
  printWindow.document.write(buildOrderReportHtml({ check, businessName, generatedBy }));
  printWindow.document.close();
  printWindow.focus();
  printWindow.print();
}

function orderQuantity(item: ParTemplateItem, currentStock: string | number | undefined) {
  const shortage = Math.max(numberValue(item.targetParLevel) - numberValue(currentStock), 0);
  return Math.ceil(shortage / Math.max(numberValue(item.unitsPerPurchasedItem), 1));
}

function shortageQuantity(item: ParTemplateItem, currentStock: string | number | undefined) {
  return Math.max(numberValue(item.targetParLevel) - numberValue(currentStock), 0);
}

function TextInput({
  value,
  onChange,
  placeholder,
  type = "text",
}: {
  value: string | number;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <input
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
      className="w-full rounded-md px-3 py-2 text-sm outline-none"
      style={{ border: `1px solid ${LINE}`, color: NAVY, backgroundColor: SURFACE }}
    />
  );
}

function Field({
  label,
  children,
  className = "",
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={`block ${className}`}>
      <span className="block mb-1.5 text-[10px] font-semibold uppercase" style={{ color: MUTED }}>
        {label}
      </span>
      {children}
    </label>
  );
}

function HeaderWithTooltip({
  label,
  description,
}: {
  label: string;
  description?: string;
}) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span>{label}</span>
      {description ? (
        <span className="group relative inline-flex">
          <span
            tabIndex={0}
            aria-label={`${label}: ${description}`}
            className="inline-flex h-3.5 w-3.5 cursor-help items-center justify-center rounded-full"
            style={{ color: MUTED, backgroundColor: CHIP, border: `1px solid ${LINE}` }}
          >
            <Info size={10} />
          </span>
          <span
            role="tooltip"
            className="pointer-events-none absolute left-1/2 top-full z-10 mt-2 w-52 -translate-x-1/2 rounded-md px-2 py-1.5 text-[10px] normal-case tracking-normal opacity-0 shadow-md transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
            style={{ color: "white", backgroundColor: NAVY }}
          >
            {description}
          </span>
        </span>
      ) : null}
    </span>
  );
}

function buildTemplateItemForm(item: ParTemplateItem) {
  return {
    itemName: item.itemName,
    supplierItemDescription: item.supplierItemDescription,
    kitchenUnit: item.kitchenUnit,
    unitsPerPurchasedItem: String(item.unitsPerPurchasedItem),
    targetParLevel: String(item.targetParLevel),
    supplierId: item.supplierId,
    category: item.category,
    note: item.note,
    isActive: item.isActive,
  };
}

type TemplateItemForm = ReturnType<typeof buildTemplateItemForm>;
const GENERAL_SECTION = "General";

function sectionName(value?: string | null) {
  return value?.trim() || GENERAL_SECTION;
}

function TemplateItemRow({
  item,
  form,
  isDirty,
  onChange,
  suppliers,
  businessId,
  sections,
}: {
  item: ParTemplateItem;
  form: TemplateItemForm;
  isDirty: boolean;
  onChange: (updater: (prev: TemplateItemForm) => TemplateItemForm) => void;
  suppliers: { id: string; name: string }[];
  businessId?: string;
  sections: string[];
}) {
  const queryClient = useQueryClient();
  const supplierItemsQuery = useQuery<ParSupplierItem[]>({
    queryKey: ["par-supplier-items", businessId, form.supplierId],
    queryFn: () => fetchParSupplierItems(form.supplierId, businessId),
    enabled: Boolean(businessId) && Boolean(form.supplierId),
    ...PAR_QUERY_OPTIONS,
  });

  const supplierItems: ParSupplierItem[] = supplierItemsQuery.data ?? [];

  const deleteMutation = useMutation({
    mutationFn: () => deleteParTemplateItem(item.id, businessId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["par-template", businessId] });
    },
  });

  return (
    <tr style={{ borderBottom: `1px solid ${LINE}` }}>
      <td className="px-3 py-3 w-[160px]">
        <TextInput
          value={form.itemName}
          onChange={(value) => onChange((prev) => ({ ...prev, itemName: value }))}
          placeholder="Chorizo"
        />
      </td>
      <td className="px-3 py-3 w-[160px]">
        <select
          value={form.supplierId}
          onChange={(event) => onChange((prev) => ({ ...prev, supplierId: event.target.value, supplierItemDescription: "" }))}
          className="w-full rounded-md px-3 py-2 text-sm outline-none"
          style={{ border: `1px solid ${LINE}`, color: NAVY, backgroundColor: SURFACE }}
        >
          <option value="">Choose supplier</option>
          {suppliers.map((supplier) => (
            <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
          ))}
        </select>
      </td>
      <td className="px-3 py-3 w-[240px]">
        <select
          value={form.supplierItemDescription}
          onChange={(event) => onChange((prev) => ({ ...prev, supplierItemDescription: event.target.value }))}
          className="w-full rounded-md px-3 py-2 text-sm outline-none"
          style={{ border: `1px solid ${LINE}`, color: NAVY, backgroundColor: SURFACE }}
        >
          <option value="">{form.supplierId ? "Choose supplier item" : "Choose supplier first"}</option>
          {supplierItems.map((entry) => (
            <option key={entry.description} value={entry.description}>{entry.description}</option>
          ))}
        </select>
      </td>
      <td className="px-3 py-3 w-[130px]">
        <TextInput value={form.kitchenUnit} onChange={(value) => onChange((prev) => ({ ...prev, kitchenUnit: value }))} />
      </td>
      <td className="px-3 py-3 w-[130px]">
        <TextInput
          type="number"
          value={form.unitsPerPurchasedItem}
          onChange={(value) => onChange((prev) => ({ ...prev, unitsPerPurchasedItem: value }))}
        />
      </td>
      <td className="px-3 py-3 w-[120px]">
        <TextInput
          type="number"
          value={form.targetParLevel}
          onChange={(value) => onChange((prev) => ({ ...prev, targetParLevel: value }))}
        />
      </td>
      <td className="px-3 py-3 w-[160px]">
        <select
          value={form.category}
          onChange={(event) => onChange((prev) => ({ ...prev, category: event.target.value }))}
          className="w-full rounded-md px-3 py-2 text-sm outline-none"
          style={{ border: `1px solid ${LINE}`, color: NAVY, backgroundColor: SURFACE }}
        >
          <option value="">General</option>
          {sections.filter((section) => section !== GENERAL_SECTION).map((section) => (
            <option key={section} value={section}>{section}</option>
          ))}
        </select>
      </td>
      <td className="px-3 py-3 w-[200px]">
        <TextInput value={form.note} onChange={(value) => onChange((prev) => ({ ...prev, note: value }))} placeholder="Optional" />
      </td>
      <td className="px-3 py-3 w-[100px]">
        <button
          type="button"
          onClick={() => onChange((prev) => ({ ...prev, isActive: !prev.isActive }))}
          className="rounded-md px-3 py-2 text-xs font-bold"
          style={{
            color: form.isActive ? DOWN : NAVY,
            backgroundColor: form.isActive ? DOWN_WEAK : CHIP,
            border: `1px solid ${form.isActive ? DOWN_LINE : LINE}`,
          }}
        >
          {form.isActive ? "Active" : "Inactive"}
        </button>
      </td>
      <td className="px-3 py-3 w-[110px]">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => deleteMutation.mutate()}
            disabled={deleteMutation.isPending}
            className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-2 text-xs font-bold disabled:opacity-50"
            style={{ color: UP, border: `1px solid ${UP_LINE}`, backgroundColor: UP_WEAK }}
            title="Delete item"
          >
            <Trash2 size={13} />
            Delete
          </button>
        </div>
        {isDirty ? (
          <p className="mt-1 text-[10px]" style={{ color: ORANGE }}>Unsaved changes</p>
        ) : null}
      </td>
    </tr>
  );
}

function OrderList({
  check,
  confirmDelete,
  deleting,
  onDelete,
  onEdit,
  onGenerateOrder,
}: {
  check: ParCheck;
  confirmDelete: boolean;
  deleting: boolean;
  onDelete: (check: ParCheck) => void;
  onEdit: (check: ParCheck) => void;
  onGenerateOrder: (check: ParCheck) => void;
}) {
  const orderItems = check.items.filter((item) => item.orderQuantity > 0);

  return (
    <div className="rounded-lg overflow-hidden" style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}`, boxShadow: SHADOW }}>
      <div className="px-4 py-3 flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between" style={{ backgroundColor: RAISED, borderBottom: `1px solid ${LINE}` }}>
        <div>
          <p className="text-sm font-semibold" style={{ color: NAVY }}>Order list for {displayDate(check.checkDate)}</p>
          <p className="text-xs mt-0.5" style={{ color: MUTED }}>
            Submitted {displayDateTime(check.submittedAt)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-md px-3 py-1.5 text-xs font-bold" style={{ color: DOWN, backgroundColor: DOWN_WEAK, border: `1px solid ${DOWN_LINE}` }}>
            {check.orderItemCount} items to order
          </span>
          <button
            type="button"
            onClick={() => onEdit(check)}
            className="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-xs font-bold"
            style={{ color: NAVY, backgroundColor: CHIP, border: `1px solid ${LINE}` }}
          >
            <RotateCcw size={13} />
            Edit / re-run
          </button>
          <button
            type="button"
            onClick={() => onDelete(check)}
            disabled={deleting}
            className="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-xs font-bold disabled:opacity-50"
            style={{ color: UP, backgroundColor: confirmDelete ? "rgba(194,65,46,0.18)" : UP_WEAK, border: `1px solid ${UP_LINE}` }}
          >
            {deleting ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
            {confirmDelete ? "Confirm delete" : "Delete"}
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr style={{ backgroundColor: RAISED, borderBottom: `1px solid ${LINE}` }}>
              {["Stock Item", "Equivalent Line Item", "Kitchen Unit", "Current", "Par", "Shortage", "Units / Purchase", "Order", "Supplier"].map((header) => (
                <th key={header} className="px-4 py-3 text-left text-[10px] font-semibold uppercase" style={{ color: MUTED }}>
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {orderItems.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-10 text-center text-sm" style={{ color: MUTED }}>
                  Nothing needs ordering from this check.
                </td>
              </tr>
            ) : (
              orderItems.map((item) => (
                <tr key={item.id} style={{ borderBottom: `1px solid ${LINE}` }}>
                  <td className="px-4 py-3 text-sm font-bold" style={{ color: NAVY }}>{item.itemName}</td>
                  <td className="px-4 py-3 text-sm" style={{ color: MUTED }}>{item.supplierItemDescription}</td>
                  <td className="px-4 py-3 text-sm" style={{ color: MUTED }}>{item.kitchenUnit}</td>
                  <td className="px-4 py-3 text-sm" style={{ color: NAVY }}>{item.currentStock}</td>
                  <td className="px-4 py-3 text-sm" style={{ color: NAVY }}>{item.targetParLevel}</td>
                  <td className="px-4 py-3 text-sm font-bold" style={{ color: ORANGE }}>{item.shortageQuantity}</td>
                  <td className="px-4 py-3 text-sm" style={{ color: NAVY }}>{item.unitsPerPurchasedItem}</td>
                  <td className="px-4 py-3 text-sm font-semibold" style={{ color: ORANGE }}>{item.orderQuantity}</td>
                  <td className="px-4 py-3 text-sm" style={{ color: MUTED }}>{item.supplierName || "Not set"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="flex justify-end px-4 py-4" style={{ borderTop: `1px solid ${LINE}` }}>
        <button
          type="button"
          onClick={() => onGenerateOrder(check)}
          disabled={orderItems.length === 0}
          className="inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50"
          style={{ backgroundColor: ORANGE, boxShadow: ACCENT_SHADOW }}
        >
          <FileText size={15} />
          Generate order
        </button>
      </div>
    </div>
  );
}

export default function ParLevelPage() {
  const { activeBusiness, hasPermission } = useBusiness();
  const { user } = useAuth();
  const businessId = activeBusiness?.id;
  const canRunParChecks = hasPermission("par_check");
  const canEditParTemplate = hasPermission("par_template");
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();

  const { data: businessDetails } = useQuery({
    queryKey: ["business-details", businessId],
    queryFn: () => fetchBusinessDetails(businessId),
    enabled: Boolean(businessId),
    staleTime: 5 * 60_000,
  });
  const categories = getCategoriesForIndustry(businessDetails?.industry);
  const urlView = (new URLSearchParams(location.search).get("view") ?? "check") as ViewMode;
  const [view, setView] = useState<ViewMode>(urlView);

  useEffect(() => {
    const v = (new URLSearchParams(location.search).get("view") ?? "check") as ViewMode;
    setView(v);
  }, [location.search]);

  function changeView(v: ViewMode) {
    setView(v);
    navigate(v === "check" ? "/par-level" : `/par-level?view=${v}`, { replace: true });
  }
  const [checkDate, setCheckDate] = useState(today());
  const [stock, setStock] = useState<Record<string, string>>({});
  const [newItem, setNewItem] = useState({
    itemName: "",
    supplierId: "",
    supplierItemDescription: "",
    kitchenUnit: "each",
    unitsPerPurchasedItem: "",
    targetParLevel: "",
    category: "",
    note: "",
  });
  const [selectedCheckId, setSelectedCheckId] = useState("");
  const [confirmDeleteCheckId, setConfirmDeleteCheckId] = useState("");
  const [message, setMessage] = useState("");
  const [templateDrafts, setTemplateDrafts] = useState<Record<string, TemplateItemForm>>({});
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [customSections, setCustomSections] = useState<string[]>([]);
  const [newSectionName, setNewSectionName] = useState("");
  const [deletingSectionName, setDeletingSectionName] = useState("");
  const [confirmDeleteSectionName, setConfirmDeleteSectionName] = useState("");
  const [orderReportCheck, setOrderReportCheck] = useState<ParCheck | null>(null);

  const templateQuery = useQuery<ParTemplate>({
    queryKey: ["par-template", businessId],
    queryFn: () => fetchParTemplate(businessId),
    enabled: Boolean(businessId),
    ...PAR_QUERY_OPTIONS,
  });
  const suppliersQuery = useQuery<Supplier[]>({
    queryKey: ["suppliers", businessId],
    queryFn: () => fetchSuppliers(businessId),
    enabled: Boolean(businessId) && canEditParTemplate && view === "template",
    ...PAR_QUERY_OPTIONS,
  });
  const checksQuery = useQuery<ParCheck[]>({
    queryKey: ["par-checks", businessId],
    queryFn: () => fetchParChecks(businessId),
    enabled: Boolean(businessId) && canRunParChecks && (view === "history" || view === "check"),
    ...PAR_QUERY_OPTIONS,
  });

  const items: ParTemplateItem[] = templateQuery.data?.items ?? [];
  const activeItems = useMemo(() => items.filter((item) => item.isActive), [items]);
  const templateItems = useMemo(() => items.filter((item) => item.isActive), [items]);
  const sectionOptions = useMemo(() => {
    const names = new Set<string>([GENERAL_SECTION]);
    customSections.forEach((section) => names.add(section));
    templateItems.forEach((item) => names.add(sectionName(item.category)));
    return Array.from(names);
  }, [customSections, templateItems]);
  const templateSections = useMemo(() => {
    const sectionSet = new Set<string>([...customSections]);
    templateItems.forEach((item) => sectionSet.add(sectionName(item.category)));
    if (sectionSet.size === 0) sectionSet.add(GENERAL_SECTION);
    return Array.from(sectionSet).map((section) => ({
      name: section,
      items: templateItems.filter((item) => sectionName(item.category) === section),
    }));
  }, [customSections, templateItems]);
  const activeSections = useMemo(() => {
    const sectionSet = new Set<string>();
    activeItems.forEach((item) => sectionSet.add(sectionName(item.category)));
    if (sectionSet.size === 0) sectionSet.add(GENERAL_SECTION);
    return Array.from(sectionSet).map((section) => ({
      name: section,
      items: activeItems.filter((item) => sectionName(item.category) === section),
    }));
  }, [activeItems]);
  const suppliers: { id: string; name: string }[] = useMemo(
    () => (suppliersQuery.data ?? []).map((supplier: Supplier) => ({ id: supplier.id, name: supplier.name })),
    [suppliersQuery.data],
  );
  const checks: ParCheck[] = checksQuery.data ?? [];
  const selectedCheck = checks.find((check) => check.id === selectedCheckId) ?? checks[0];
  const checkForSelectedDate = checks.find((check) => check.checkDate === checkDate);
  const generatedBy = user?.email ?? "Unknown user";
  const businessName = activeBusiness?.name ?? "This business";
  const newSupplierItemsQuery = useQuery<ParSupplierItem[]>({
    queryKey: ["par-supplier-items", businessId, newItem.supplierId],
    queryFn: () => fetchParSupplierItems(newItem.supplierId, businessId),
    enabled: Boolean(businessId) && canEditParTemplate && view === "template" && Boolean(newItem.supplierId),
    ...PAR_QUERY_OPTIONS,
  });
  const newSupplierItems: ParSupplierItem[] = newSupplierItemsQuery.data ?? [];

  useEffect(() => {
    setTemplateDrafts(
      Object.fromEntries(items.map((item) => [item.id, buildTemplateItemForm(item)])),
    );
  }, [items]);

  useEffect(() => {
    setStock((prev) => {
      let changed = false;
      const activeIds = new Set(activeItems.map((item) => item.id));
      const next: Record<string, string> = {};

      Object.entries(prev).forEach(([id, value]) => {
        if (activeIds.has(id)) next[id] = value;
      });

      activeItems.forEach((item) => {
        if (next[item.id] === undefined) {
          next[item.id] = "";
          changed = true;
        }
      });

      if (Object.keys(next).length !== Object.keys(prev).length) changed = true;
      if (!changed) return prev;
      return next;
    });
  }, [activeItems]);

  const addMutation = useMutation({
    mutationFn: () => createParTemplateItem({ ...newItem, targetParLevel: newItem.targetParLevel || 0 }, businessId),
    onSuccess: async () => {
      setNewItem({
        itemName: "",
        supplierId: "",
        supplierItemDescription: "",
        kitchenUnit: "each",
        unitsPerPurchasedItem: "",
        targetParLevel: "",
        category: "",
        note: "",
      });
      setAddItemOpen(false);
      setMessage("Template item added.");
      await queryClient.invalidateQueries({ queryKey: ["par-template", businessId] });
    },
    onError: (error) => setMessage(error instanceof Error ? error.message : "Could not add template item."),
  });

  const saveTemplateMutation = useMutation({
    mutationFn: async () => {
      const changedItems = items.filter((item) => {
        const draft = templateDrafts[item.id];
        return draft && JSON.stringify(draft) !== JSON.stringify(buildTemplateItemForm(item));
      });

      await Promise.all(
        changedItems.map((item) => updateParTemplateItem(item.id, templateDrafts[item.id], businessId)),
      );
      return changedItems.length;
    },
    onSuccess: async (count) => {
      setMessage(count > 0 ? "Template changes saved." : "No template changes to save.");
      await queryClient.invalidateQueries({ queryKey: ["par-template", businessId] });
    },
    onError: (error) => setMessage(error instanceof Error ? error.message : "Could not save template changes."),
  });

  const deleteSectionMutation = useMutation({
    mutationFn: async ({ name, sectionItems }: { name: string; sectionItems: ParTemplateItem[] }) => {
      await Promise.all(sectionItems.map((item) => deleteParTemplateItem(item.id, businessId)));
      return name;
    },
    onSuccess: async (name) => {
      setCustomSections((prev) => prev.filter((section) => section !== name));
      setDeletingSectionName("");
      setConfirmDeleteSectionName("");
      setMessage("Section deleted.");
      await queryClient.invalidateQueries({ queryKey: ["par-template", businessId] });
    },
    onError: (error) => {
      setDeletingSectionName("");
      setConfirmDeleteSectionName("");
      setMessage(error instanceof Error ? error.message : "Could not delete section.");
    },
  });

  const submitMutation = useMutation({
    mutationFn: () =>
      submitParCheck({
        checkDate,
        items: activeItems.map((item) => ({ itemId: item.id, currentStock: stock[item.id] || 0 })),
      }, businessId),
    onSuccess: async (check) => {
      setMessage("Par check saved.");
      setSelectedCheckId(check.id);
      queryClient.setQueryData<ParCheck[]>(["par-checks", businessId], (current: ParCheck[] | undefined) => {
        const withoutDuplicate = (current ?? []).filter((item) => item.id !== check.id);
        return [check, ...withoutDuplicate];
      });
      changeView("history");
    },
    onError: (error) => setMessage(error instanceof Error ? error.message : "Could not save par check."),
  });

  const deleteCheckMutation = useMutation({
    mutationFn: (checkId: string) => deleteParCheck(checkId, businessId),
    onSuccess: ({ id }) => {
      queryClient.setQueryData<ParCheck[]>(["par-checks", businessId], (current: ParCheck[] | undefined) =>
        (current ?? []).filter((check) => check.id !== id),
      );
      setSelectedCheckId((current) => (current === id ? "" : current));
      setConfirmDeleteCheckId("");
      setMessage("Par check deleted.");
    },
    onError: (error) => setMessage(error instanceof Error ? error.message : "Could not delete par check."),
  });

  function handleAddItem(event: FormEvent) {
    event.preventDefault();
    setMessage("");
    if (!newItem.itemName.trim()) {
      setMessage("Stock check item is required.");
      return;
    }
    if (!newItem.supplierId) {
      setMessage("Choose a supplier.");
      return;
    }
    if (!newItem.supplierItemDescription) {
      setMessage("Choose a supplier item.");
      return;
    }
    if (!newItem.kitchenUnit.trim()) {
      setMessage("Kitchen stock unit is required.");
      return;
    }
    if (numberValue(newItem.unitsPerPurchasedItem) <= 0) {
      setMessage("Units per purchased item must be greater than 0.");
      return;
    }
    addMutation.mutate();
  }

  function openAddItem(section = GENERAL_SECTION) {
    setMessage("");
    setNewItem((prev) => ({
      ...prev,
      category: section === GENERAL_SECTION ? "" : section,
    }));
    setAddItemOpen(true);
  }

  function handleAddSection(event: FormEvent) {
    event.preventDefault();
    const next = newSectionName.trim();
    if (!next) return;
    setCustomSections((prev) => (
      prev.some((section) => section.toLowerCase() === next.toLowerCase()) ? prev : [...prev, next]
    ));
    setNewSectionName("");
    openAddItem(next);
  }

  function handleDeleteSection(section: { name: string; items: ParTemplateItem[] }) {
    setMessage("");
    if (section.items.length === 0) {
      setCustomSections((prev) => prev.filter((name) => name !== section.name));
      setConfirmDeleteSectionName("");
      setMessage("Empty section removed.");
      return;
    }

    if (confirmDeleteSectionName !== section.name) {
      setConfirmDeleteSectionName(section.name);
      setMessage(`Click Confirm delete to remove "${section.name}" and ${section.items.length} item${section.items.length === 1 ? "" : "s"}.`);
      return;
    }

    setDeletingSectionName(section.name);
    deleteSectionMutation.mutate({ name: section.name, sectionItems: section.items });
  }

  function handleSubmitCheck(event: FormEvent) {
    event.preventDefault();
    setMessage("");
    if (activeItems.length === 0) {
      setMessage("Add active template items before submitting a check.");
      return;
    }
    submitMutation.mutate();
  }

  function loadCheckIntoDailyForm(check: ParCheck, targetDate: string, actionLabel: string) {
    if (activeItems.length === 0) {
      setMessage("Add active template items before loading a past check.");
      return;
    }

    const byTemplateId = new Map(check.items.map((item) => [item.templateItemId, item]));
    const byLabel = new Map(check.items.map((item) => [`${item.itemName}||${item.supplierItemDescription}`, item]));
    let matchedCount = 0;
    const nextStock: Record<string, string> = {};

    activeItems.forEach((item) => {
      const snapshotItem = byTemplateId.get(item.id) ?? byLabel.get(`${item.itemName}||${item.supplierItemDescription}`);
      if (snapshotItem) {
        nextStock[item.id] = String(snapshotItem.currentStock);
        matchedCount += 1;
      } else {
        nextStock[item.id] = "";
      }
    });

    setStock(nextStock);
    setCheckDate(targetDate);
    setConfirmDeleteCheckId("");
    changeView("check");
    setMessage(
      matchedCount === check.items.length
        ? `${actionLabel} loaded. Submit to save it as a new par check.`
        : `${actionLabel} loaded for ${matchedCount} matching active items. Submit to save it as a new par check.`,
    );
  }

  function handleEditCheck(check: ParCheck) {
    loadCheckIntoDailyForm(check, check.checkDate, "Past check");
  }

  function handleDuplicateCheck(check: ParCheck) {
    loadCheckIntoDailyForm(check, today(), "Duplicate check");
  }

  function handleDeleteCheck(check: ParCheck) {
    setMessage("");
    if (confirmDeleteCheckId !== check.id) {
      setConfirmDeleteCheckId(check.id);
      setMessage("This will permanently delete the saved par check. Click Confirm delete to continue.");
      return;
    }
    deleteCheckMutation.mutate(check.id);
  }

  const projectedOrderCount = activeItems.filter((item) => orderQuantity(item, stock[item.id]) > 0).length;
  const projectedOrderQuantity = activeItems.reduce((sum, item) => sum + orderQuantity(item, stock[item.id]), 0);
  const dirtyTemplateItemIds = useMemo(
    () =>
      items
        .filter((item) => {
          const draft = templateDrafts[item.id];
          return draft && JSON.stringify(draft) !== JSON.stringify(buildTemplateItemForm(item));
        })
        .map((item) => item.id),
    [items, templateDrafts],
  );
  const dirtyTemplateItemIdSet = useMemo(() => new Set(dirtyTemplateItemIds), [dirtyTemplateItemIds]);
  const dailyCheckHeaders: { label: string; description?: string }[] = [
    { label: "Stock Item" },
    { label: "Kitchen Unit" },
    { label: "Current stock", description: "The amount you currently have in the kitchen, entered in kitchen units." },
    { label: "Shortage", description: "The gap between current stock and target par." },
    { label: "Equivalent Line Item" },
    { label: "Units / Purchase", description: "How many kitchen units you get from one purchased supplier item." },
    { label: "Target par", description: "The ideal amount of stock you want to have on hand after ordering." },
    { label: "Order", description: "Suggested supplier items to order, rounded up from the shortage." },
  ];

  const parNavItems = [
    ...(canRunParChecks ? [
      { value: "check" as const, label: "Daily check", icon: ClipboardList },
      { value: "history" as const, label: "History", icon: History },
    ] : []),
    ...(canEditParTemplate ? [
      { value: "template" as const, label: "Template", icon: PackageCheck },
    ] : []),
  ];

  useEffect(() => {
    if (view === "template" && !canEditParTemplate) {
      setView(canRunParChecks ? "check" : "template");
      return;
    }
    if ((view === "check" || view === "history") && !canRunParChecks && canEditParTemplate) {
      setView("template");
    }
  }, [canEditParTemplate, canRunParChecks, view]);

  return (
    <div
      className="flex h-full"
      style={{
        backgroundColor: CANVAS,
        minHeight: "calc(100vh - 73px)",
        fontFamily: "var(--font-ui)",
      }}
    >
      <main className="flex-1 overflow-y-auto p-6 space-y-4" style={{ backgroundColor: CANVAS }}>
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h2 className="text-[27px] font-semibold leading-tight" style={{ color: NAVY }}>
              {parNavItems.find((item) => item.value === view)?.label}
            </h2>
            <p className="text-sm mt-0.5" style={{ color: MUTED }}>
              Daily stock checks and suggested ordering for {activeBusiness?.name ?? "this business"}.
            </p>
          </div>

          {view === "check" && (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end lg:pt-0.5">
              {checkForSelectedDate && (
                <div
                  className="rounded-lg px-3 py-2 text-xs"
                  style={{ border: `1px solid ${LINE}`, color: NAVY, backgroundColor: SURFACE }}
                >
                  <span style={{ color: MUTED }}>Saved check exists.</span>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedCheckId(checkForSelectedDate.id);
                      setConfirmDeleteCheckId("");
                      changeView("history");
                    }}
                    className="ml-2 font-bold"
                    style={{ color: ORANGE }}
                  >
                    View check
                  </button>
                </div>
              )}
              <label className="text-xs font-semibold" style={{ color: MUTED }}>
                Check date
                <input
                  type="date"
                  value={checkDate}
                  onChange={(event) => setCheckDate(event.target.value)}
                  className="mt-1 block rounded-md px-3 py-2 text-sm outline-none"
                  style={{ border: `1px solid ${LINE}`, color: NAVY, backgroundColor: SURFACE }}
                />
              </label>
              <button
                type="submit"
                form="daily-check-form"
                disabled={submitMutation.isPending || activeItems.length === 0}
                className="inline-flex items-center justify-center gap-2 rounded-md px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50"
                style={{ backgroundColor: ORANGE, boxShadow: ACCENT_SHADOW }}
              >
                {submitMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                Finish check
              </button>
            </div>
          )}
        </div>

        {(templateQuery.error || checksQuery.error) && (
          <div className="rounded-lg px-4 py-3 text-sm" style={{ color: UP, backgroundColor: UP_WEAK, border: `1px solid ${UP_LINE}` }}>
            {(templateQuery.error as Error)?.message || (checksQuery.error as Error)?.message}
          </div>
        )}

        {message && (
          <div
            className="rounded-lg px-4 py-3 text-sm"
            style={{
              color: message.includes("Could") || message.includes("required") ? UP : DOWN,
              backgroundColor: message.includes("Could") || message.includes("required") ? UP_WEAK : DOWN_WEAK,
              border: `1px solid ${message.includes("Could") || message.includes("required") ? UP_LINE : DOWN_LINE}`,
            }}
          >
            {message}
          </div>
        )}

        {addItemOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ backgroundColor: "rgba(80,55,30,0.36)" }}
            onClick={() => setAddItemOpen(false)}
          >
            <form
              onSubmit={handleAddItem}
              className="w-full max-w-3xl rounded-2xl p-5"
              style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}`, boxShadow: "0 24px 70px rgba(80,55,30,0.22)" }}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold" style={{ color: NAVY }}>Add check item</h2>
                  <p className="text-xs mt-1" style={{ color: MUTED }}>
                    Add an item to a par check section and map it to a supplier line item.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setAddItemOpen(false)}
                  className="rounded-lg px-3 py-2 text-xs font-bold"
                  style={{ border: `1px solid ${LINE}`, color: NAVY }}
                >
                  Close
                </button>
              </div>

              <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2">
                <Field label="Section">
                  <select
                    value={newItem.category || GENERAL_SECTION}
                    onChange={(event) => setNewItem((prev) => ({ ...prev, category: event.target.value === GENERAL_SECTION ? "" : event.target.value }))}
                    className="w-full rounded-md px-3 py-2 text-sm outline-none"
                    style={{ border: `1px solid ${LINE}`, color: NAVY, backgroundColor: SURFACE }}
                  >
                    {sectionOptions.map((section) => (
                      <option key={section} value={section}>{section}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Stock item">
                  <TextInput
                    value={newItem.itemName}
                    onChange={(value) => setNewItem((prev) => ({ ...prev, itemName: value }))}
                    placeholder="Chorizo"
                  />
                </Field>
                <Field label="Supplier">
                  <select
                    value={newItem.supplierId}
                    onChange={(event) => setNewItem((prev) => ({ ...prev, supplierId: event.target.value, supplierItemDescription: "" }))}
                    className="w-full rounded-md px-3 py-2 text-sm outline-none"
                    style={{ border: `1px solid ${LINE}`, color: NAVY, backgroundColor: SURFACE }}
                  >
                    <option value="">Choose supplier</option>
                    {suppliers.map((supplier) => <option key={supplier.id} value={supplier.id}>{supplier.name}</option>)}
                  </select>
                </Field>
                <Field label="Equivalent line item">
                  <select
                    value={newItem.supplierItemDescription}
                    onChange={(event) => setNewItem((prev) => ({ ...prev, supplierItemDescription: event.target.value }))}
                    className="w-full rounded-md px-3 py-2 text-sm outline-none"
                    style={{ border: `1px solid ${LINE}`, color: NAVY, backgroundColor: SURFACE }}
                  >
                    <option value="">{newItem.supplierId ? "Choose line item" : "Choose supplier first"}</option>
                    {newSupplierItems.map((entry) => <option key={entry.description} value={entry.description}>{entry.description}</option>)}
                  </select>
                </Field>
                <Field label="Kitchen unit">
                  <TextInput value={newItem.kitchenUnit} onChange={(value) => setNewItem((prev) => ({ ...prev, kitchenUnit: value }))} placeholder="kg, pack, bottle" />
                </Field>
                <Field label="Units / purchase">
                  <TextInput
                    type="number"
                    value={newItem.unitsPerPurchasedItem}
                    onChange={(value) => setNewItem((prev) => ({ ...prev, unitsPerPurchasedItem: value }))}
                    placeholder="10"
                  />
                </Field>
                <Field label="Target par">
                  <TextInput type="number" value={newItem.targetParLevel} onChange={(value) => setNewItem((prev) => ({ ...prev, targetParLevel: value }))} placeholder="18" />
                </Field>
                <Field label="Note">
                  <TextInput value={newItem.note} onChange={(value) => setNewItem((prev) => ({ ...prev, note: value }))} placeholder="Optional note" />
                </Field>
              </div>

              <div className="mt-5 flex justify-end">
                <button
                  type="submit"
                  disabled={addMutation.isPending}
                  className="inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50"
                  style={{ backgroundColor: ORANGE, boxShadow: ACCENT_SHADOW }}
                >
                  {addMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                  Add item
                </button>
              </div>
            </form>
          </div>
        )}

        {orderReportCheck && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ backgroundColor: "rgba(80,55,30,0.36)" }}
            onClick={() => setOrderReportCheck(null)}
          >
            <div
              className="flex max-h-[88vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl"
              style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}`, boxShadow: "0 24px 70px rgba(80,55,30,0.22)" }}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-4 px-5 py-4" style={{ borderBottom: `1px solid ${LINE}` }}>
                <div>
                  <h2 className="text-lg font-semibold" style={{ color: NAVY }}>Order report preview</h2>
                  <p className="text-xs mt-1" style={{ color: MUTED }}>
                    {displayDate(orderReportCheck.checkDate)} · Generated by {generatedBy}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => printOrderReport(orderReportCheck, businessName, generatedBy)}
                    className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-bold text-white"
                    style={{ backgroundColor: ORANGE, boxShadow: ACCENT_SHADOW }}
                  >
                    <Download size={13} />
                    Download PDF
                  </button>
                  <button
                    type="button"
                    onClick={() => setOrderReportCheck(null)}
                    className="rounded-lg px-3 py-2 text-xs font-bold"
                    style={{ border: `1px solid ${LINE}`, color: NAVY }}
                  >
                    Close
                  </button>
                </div>
              </div>
              <div className="overflow-y-auto p-5">
                <div className="mb-5 rounded-xl p-4" style={{ border: `1px solid ${LINE}` }}>
                  <p className="text-2xl font-semibold" style={{ color: NAVY }}>Order report</p>
                  <div className="mt-2 grid grid-cols-1 gap-1 text-xs sm:grid-cols-2" style={{ color: MUTED }}>
                    <p>Date: {displayDate(orderReportCheck.checkDate)}</p>
                    <p>Generated by: {generatedBy}</p>
                    <p>Business: {businessName}</p>
                    <p>Generated: {new Date().toLocaleString()}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {groupOrderItems(orderReportCheck).length === 0 ? (
                    <p className="text-sm" style={{ color: MUTED }}>No order items for this check.</p>
                  ) : groupOrderItems(orderReportCheck).map(({ supplier, items }) => (
                    <section key={supplier} className="overflow-hidden rounded-xl" style={{ border: `1px solid ${LINE}` }}>
                      <div className="px-4 py-3" style={{ backgroundColor: RAISED, borderBottom: `1px solid ${LINE}` }}>
                        <p className="text-sm font-semibold" style={{ color: NAVY }}>{supplier}</p>
                        <p className="text-xs mt-0.5" style={{ color: MUTED }}>Total estimated order value: Not available</p>
                      </div>
                      <table className="w-full">
                        <thead>
                          <tr style={{ borderBottom: `1px solid ${LINE}` }}>
                            {["Item", "Order line item", "Order qty", "Unit"].map((header) => (
                              <th key={header} className="px-4 py-3 text-left text-[10px] font-semibold uppercase" style={{ color: MUTED }}>
                                {header}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((item) => (
                            <tr key={item.id} style={{ borderBottom: "1px solid rgba(80,55,30,0.07)" }}>
                              <td className="px-4 py-3 text-sm font-bold" style={{ color: NAVY }}>{item.itemName}</td>
                              <td className="px-4 py-3 text-sm" style={{ color: NAVY, opacity: 0.65 }}>{item.supplierItemDescription}</td>
                              <td className="px-4 py-3 text-sm font-extrabold" style={{ color: ORANGE }}>{item.orderQuantity}</td>
                              <td className="px-4 py-3 text-sm" style={{ color: NAVY }}>{item.kitchenUnit}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </section>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {templateQuery.isLoading ? (
          <div className="py-24 text-center">
            <Loader2 size={26} className="mx-auto animate-spin" style={{ color: ORANGE }} />
            <p className="text-sm mt-3" style={{ color: NAVY, opacity: 0.5 }}>Loading par levels...</p>
          </div>
        ) : view === "template" ? (
          <div className="space-y-4">
            <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}`, boxShadow: SHADOW }}>
              <div className="flex flex-col gap-4 px-5 py-4 lg:flex-row lg:items-center lg:justify-between" style={{ backgroundColor: RAISED, borderBottom: `1px solid ${LINE}` }}>
                <div>
                  <p className="text-sm font-semibold" style={{ color: NAVY }}>Par check sections</p>
                  <p className="text-xs mt-0.5" style={{ color: MUTED }}>
                    {dirtyTemplateItemIds.length > 0 ? `${dirtyTemplateItemIds.length} item${dirtyTemplateItemIds.length === 1 ? "" : "s"} with unsaved changes` : "All changes saved"}
                  </p>
                </div>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <form onSubmit={handleAddSection} className="flex gap-2">
                    <input
                      value={newSectionName}
                      onChange={(event) => setNewSectionName(event.target.value)}
                      placeholder="New section"
                      className="w-40 rounded-lg px-3 py-2 text-sm outline-none"
                      style={{ border: `1px solid ${LINE}`, color: NAVY, backgroundColor: SURFACE }}
                    />
                    <button type="submit" className="rounded-lg px-3 py-2 text-xs font-bold" style={{ border: `1px solid ${LINE}`, color: NAVY, backgroundColor: CHIP }}>
                      Add section
                    </button>
                  </form>
                  <button
                    type="button"
                    onClick={() => {
                      setMessage("");
                      saveTemplateMutation.mutate();
                    }}
                    disabled={saveTemplateMutation.isPending || dirtyTemplateItemIds.length === 0}
                    className="inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
                    style={{ backgroundColor: ORANGE, boxShadow: ACCENT_SHADOW }}
                  >
                    {saveTemplateMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    Save changes
                  </button>
                </div>
              </div>

              <div className="space-y-4 p-5">
                {templateSections.map((section) => (
                  <section key={section.name} className="rounded-2xl overflow-hidden" style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}` }}>
                    <div
                      className="flex items-center justify-between gap-3 px-4 py-3"
                      style={{ borderBottom: `1px solid ${LINE}`, backgroundColor: RAISED }}
                    >
                      <div>
                        <p className="text-sm font-semibold" style={{ color: NAVY }}>{section.name}</p>
                        <p className="text-xs mt-0.5" style={{ color: MUTED }}>{section.items.length} item{section.items.length === 1 ? "" : "s"}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => openAddItem(section.name)}
                          className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-bold"
                          style={{ border: `1px solid ${LINE}`, color: NAVY, backgroundColor: SURFACE }}
                        >
                          <Plus size={13} />
                          Add item
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteSection(section)}
                          disabled={deletingSectionName === section.name}
                          className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-bold disabled:opacity-50"
                          style={{
                            border: `1px solid ${confirmDeleteSectionName === section.name ? UP : UP_LINE}`,
                            color: confirmDeleteSectionName === section.name ? "white" : UP,
                            backgroundColor: confirmDeleteSectionName === section.name ? UP : UP_WEAK,
                          }}
                        >
                          {deletingSectionName === section.name ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                          {confirmDeleteSectionName === section.name ? "Confirm delete" : "Delete section"}
                        </button>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[1580px] table-fixed">
                        <thead>
                          <tr style={{ borderBottom: `1px solid ${LINE}` }}>
                            {["Stock Item", "Supplier", "Equivalent Line Item", "Kitchen Unit", "Units / Purchase", "Target", "Section", "Note", "Status", "Actions"].map((header) => (
                              <th key={header} className="px-3 py-3 text-left text-[10px] font-semibold uppercase" style={{ color: MUTED }}>
                                {header}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {section.items.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="px-4 py-12 text-center text-sm" style={{ color: NAVY, opacity: 0.45 }}>
                          Add the first item for this section.
                        </td>
                      </tr>
                    ) : (
                      section.items.map((item) => (
                        <TemplateItemRow
                          key={item.id}
                          item={item}
                          form={templateDrafts[item.id] ?? buildTemplateItemForm(item)}
                          isDirty={dirtyTemplateItemIdSet.has(item.id)}
                          onChange={(updater) => {
                            setMessage("");
                            setTemplateDrafts((prev) => {
                              const current = prev[item.id] ?? buildTemplateItemForm(item);
                              return { ...prev, [item.id]: updater(current) };
                            });
                          }}
                          suppliers={suppliers}
                          businessId={businessId}
                          sections={sectionOptions}
                        />
                      ))
                    )}
                        </tbody>
                      </table>
                    </div>
                  </section>
                ))}
              </div>
            </div>
          </div>
        ) : view === "check" ? (
          <form id="daily-check-form" onSubmit={handleSubmitCheck} className="space-y-4">
            <div className="space-y-4">
              {activeItems.length === 0 ? (
                <div className="rounded-lg px-4 py-12 text-center text-sm" style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}`, color: MUTED }}>
                  Add active supplier mappings before running a daily check.
                </div>
              ) : activeSections.map((section) => (
                <section key={section.name} className="rounded-lg overflow-hidden" style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}`, boxShadow: SHADOW }}>
                  <div className="px-4 py-3" style={{ borderBottom: `1px solid ${LINE}`, backgroundColor: RAISED }}>
                    <p className="text-sm font-semibold" style={{ color: NAVY }}>{section.name}</p>
                    <p className="text-xs mt-0.5" style={{ color: MUTED }}>{section.items.length} item{section.items.length === 1 ? "" : "s"} to check</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[1120px] table-fixed">
                      <colgroup>
                        <col style={{ width: "14%" }} />
                        <col style={{ width: "11%" }} />
                        <col style={{ width: "12%" }} />
                        <col style={{ width: "12%" }} />
                        <col style={{ width: "25%" }} />
                        <col style={{ width: "13%" }} />
                        <col style={{ width: "11%" }} />
                        <col style={{ width: "10%" }} />
                      </colgroup>
                      <thead>
                        <tr style={{ borderBottom: `1px solid ${LINE}` }}>
                          {dailyCheckHeaders.map(({ label, description }) => (
                            <th key={label} className="px-4 py-3 text-left text-[10px] font-semibold uppercase whitespace-nowrap" style={{ color: MUTED }}>
                              <HeaderWithTooltip label={label} description={description} />
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {section.items.map((item) => {
                          const shortage = shortageQuantity(item, stock[item.id]);
                          const suggested = orderQuantity(item, stock[item.id]);
                          return (
                            <tr key={item.id} style={{ borderBottom: "1px solid rgba(80,55,30,0.07)" }}>
                              <td className="px-4 py-3 text-sm font-bold" style={{ color: NAVY }}>{item.itemName}</td>
                              <td className="px-4 py-3 text-sm whitespace-nowrap" style={{ color: NAVY, opacity: 0.65 }}>{item.kitchenUnit}</td>
                              <td className="px-4 py-3">
                                <TextInput
                                  type="number"
                                  value={stock[item.id] ?? ""}
                                  onChange={(value) => setStock((prev) => ({ ...prev, [item.id]: value }))}
                                />
                              </td>
                              <td
                                className="px-4 py-3 text-sm font-bold whitespace-nowrap"
                                style={{ color: shortage > 0 ? ORANGE : NAVY }}
                              >
                                {shortage} {item.kitchenUnit}
                              </td>
                              <td className="px-4 py-3 text-sm leading-snug" style={{ color: NAVY, opacity: 0.65 }}>{item.supplierItemDescription}</td>
                              <td className="px-4 py-3 text-sm whitespace-nowrap" style={{ color: NAVY }}>{item.unitsPerPurchasedItem}</td>
                              <td className="px-4 py-3 text-sm whitespace-nowrap" style={{ color: NAVY }}>{item.targetParLevel}</td>
                              <td
                                className="px-4 py-3 text-sm font-extrabold text-center whitespace-nowrap"
                                style={{ color: suggested > 0 ? ORANGE : NAVY, backgroundColor: suggested > 0 ? "rgba(96,120,64,0.10)" : "rgba(80,55,30,0.04)" }}
                              >
                                {suggested}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </section>
              ))}
            </div>
          </form>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
            <div className="rounded-lg overflow-hidden self-start" style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}`, boxShadow: SHADOW }}>
              <div className="px-4 py-3" style={{ backgroundColor: RAISED, borderBottom: `1px solid ${LINE}` }}>
                <p className="text-sm font-semibold" style={{ color: NAVY }}>Past checks</p>
              </div>
              {checksQuery.isLoading ? (
                <div className="p-6 text-center"><Loader2 size={20} className="mx-auto animate-spin" style={{ color: ORANGE }} /></div>
              ) : checks.length === 0 ? (
                <p className="p-4 text-sm" style={{ color: NAVY, opacity: 0.45 }}>No par checks saved yet.</p>
              ) : (
                <div className="p-2 space-y-1">
                  {checks.map((check) => {
                    const active = selectedCheck?.id === check.id;
                    return (
                      <button
                        key={check.id}
                        type="button"
                        onClick={() => {
                          setSelectedCheckId(check.id);
                          setConfirmDeleteCheckId("");
                        }}
                        className="w-full rounded-md px-3 py-2 text-left"
                        style={{ backgroundColor: active ? "rgba(96,120,64,0.1)" : "transparent", color: NAVY }}
                      >
                        <span className="block text-sm font-bold">{displayDate(check.checkDate)}</span>
                        <span className="block text-xs" style={{ opacity: 0.5 }}>{check.orderItemCount} items to order</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            {selectedCheck ? (
              <OrderList
                check={selectedCheck}
                confirmDelete={confirmDeleteCheckId === selectedCheck.id}
                deleting={deleteCheckMutation.isPending && confirmDeleteCheckId === selectedCheck.id}
                onDelete={handleDeleteCheck}
                onEdit={handleEditCheck}
                onGenerateOrder={setOrderReportCheck}
              />
            ) : (
              <div className="rounded-lg p-10 text-center" style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}`, boxShadow: SHADOW }}>
                <History size={28} className="mx-auto mb-3" style={{ color: NAVY, opacity: 0.25 }} />
                <p className="text-sm" style={{ color: NAVY, opacity: 0.45 }}>Submit a daily check to create an order list.</p>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
